/**
 * Screening Service API Client — Sanctions + PEP + adverse-media screening.
 * TASK-514. Calls the real aml-service screening endpoint via the gateway.
 *
 * SECURITY: goes through the gateway (httpClient injects auth + X-Account-ID).
 * Posture: every result is a *candidate for human review*, never a confirmed
 * determination.
 */

import { httpClient } from '../lib/httpClient';

// Backend statuses (screening_service.build_category_results / adverse media)
// not_screened = the screen did NOT run (missing/invalid key or service error). It is
// NOT clear — never render it as a green pass.
export type ScreeningCategoryStatus = 'clear' | 'potential_match' | 'not_screened';
export type AdverseMediaStatus = 'clear' | 'potential_match' | 'not_screened';

export interface ScreeningMatch {
  /** OpenSanctions entity id (`Q7747`) — stable across runs, which is what makes a decision
   *  about it mean the same thing tomorrow. TASK-932. */
  entity_id?: string;
  caption: string;
  score: number;
  is_strong_match: boolean;
  categories: string[];
  topics: string[];
  datasets: string[];
  birth_date?: string | null;
  nationalities: string[];
  url?: string | null;
  verdict?: { keep: boolean; confidence: number; reason: string };
  /** TASK-932: the firm's CURRENT decision on this entity, stamped by the server. Absent =
   *  undecided. Read, never a filter: a decided false positive is still a match that was seen. */
  decision?: MatchDecisionSummary;
}

export type MatchDecisionKind = 'false_positive' | 'confirmed' | 'escalated';

export interface MatchDecisionSummary {
  decision: MatchDecisionKind;
  rationale?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
}

export interface CategoryResult {
  status: ScreeningCategoryStatus;
  matches: ScreeningMatch[];
  reviewed_count: number;
}

export interface AdverseMediaMatch {
  title?: string;
  url?: string;
  source?: string;
  sentiment?: number | null;
  published_at?: string;
}

export interface AdverseMediaResult {
  status: AdverseMediaStatus;
  note?: string;
  reviewed_count?: number;
  matches: AdverseMediaMatch[];
}

export interface ScreeningResponse {
  client_id?: string | null;
  /** TASK-932: the audit row this run wrote — what a decision cites. */
  audit_id?: string | null;
  subject_role?: 'party' | 'beneficial_owner';
  screened_at: string;
  provider: string;
  identity: { name: string; birth_date?: string | null; nationality?: string | null };
  categories: { sanctions: CategoryResult; pep: CategoryResult };
  adverse_media: AdverseMediaResult;
  disclaimer: string;
}

export interface ScreenPersonInput {
  name: string;
  birthDate?: string;
  nationality?: string;
  clientId?: string;
}

export interface BeneficialOwnerScreening {
  subject: { name: string; birth_date?: string | null; nationality?: string | null };
  screening: ScreeningResponse;
}

export interface ClientScreeningResponse {
  client_id: string;
  party: ScreeningResponse | null;
  beneficial_owners: BeneficialOwnerScreening[];
}

/**
 * Screen a person against sanctions + PEP + adverse media (audited).
 * `name` required; birthDate/nationality optional but reduce false positives.
 */
export const screenPerson = async (input: ScreenPersonInput): Promise<ScreeningResponse> => {
  return httpClient.post<ScreeningResponse>('/api/aml/screening/screen', {
    name: input.name,
    birthDate: input.birthDate,
    nationality: input.nationality,
    clientId: input.clientId,
  });
};

/**
 * Screen a client's contracting party + every beneficial owner (audited).
 * Identity comes from the KG; the fallback identity covers clients with no KG data.
 */
export const screenClient = async (
  clientId: string,
  fallback: { name?: string; birthDate?: string; nationality?: string } = {},
): Promise<ClientScreeningResponse> => {
  return httpClient.post<ClientScreeningResponse>(
    `/api/aml/screening/client/${encodeURIComponent(clientId)}`,
    { name: fallback.name, birthDate: fallback.birthDate, nationality: fallback.nationality },
  );
};

// ── TASK-932: decisions, history, monitor ──────────────────────────────────────────────

export interface MatchDecision extends MatchDecisionSummary {
  id: string;
  client_id: string;
  subject_role: string;
  provider_entity_id: string;
  audit_id?: string | null;
}

export interface DecideMatchInput {
  clientId: string;
  subjectRole: 'party' | 'beneficial_owner';
  decision: MatchDecisionKind;
  /** Required. The server refuses a blank one — a decision with no reason is a checkbox. */
  rationale: string;
  auditId?: string | null;
}

/**
 * Record the firm's decision on one provider entity for one client. CO or admin only —
 * the server gates on the JWT role and returns 403 otherwise. After the write the server
 * retracts the client's Radar subject if nothing remains open; `radar_retracted` says whether
 * that reached alert-service (null = it did not, which is not the same as 0).
 */
export const decideMatch = async (
  entityId: string,
  input: DecideMatchInput,
): Promise<{ decision: MatchDecision; open_match_ids: string[]; radar_retracted: number | null }> => {
  return httpClient.post(
    `/api/aml/screening/matches/${encodeURIComponent(entityId)}/decide`,
    {
      clientId: input.clientId,
      subjectRole: input.subjectRole,
      decision: input.decision,
      rationale: input.rationale,
      auditId: input.auditId ?? null,
    },
  );
};

export interface ScreeningRun {
  id: string;
  client_id: string;
  subject_role: string;
  subject_name: string;
  overall_status: 'clear' | 'potential_match' | 'not_screened';
  screened_at: string;
}

export interface ScreeningHistory {
  client_id: string;
  runs: ScreeningRun[];
  decisions: MatchDecision[];
}

/** Every run and every decision for a client, each newest first. */
export const getScreeningHistory = async (clientId: string): Promise<ScreeningHistory> => {
  return httpClient.get<ScreeningHistory>(
    `/api/aml/screening/client/${encodeURIComponent(clientId)}/history`,
  );
};

export interface MonitorStatus {
  /** Configuration: SCREENING_MONITORING_ENABLED. */
  enabled: boolean;
  /** Whether the scheduler is actually live in the running process. */
  scheduled: boolean;
  interval_min: number;
  /** What the last sweep recorded about itself; null if it has never run. */
  last_run: {
    clients_screened: number;
    new_alerts: number;
    retracted: number;
    started_at: string;
    finished_at: string;
  } | null;
}

/** Whether ongoing screening is on, and when it last actually ran — two different facts. */
export const getMonitorStatus = async (): Promise<MonitorStatus> => {
  return httpClient.get<MonitorStatus>('/api/aml/screening/monitor');
};

const screeningService = {
  screenPerson, screenClient, decideMatch, getScreeningHistory, getMonitorStatus,
};
export default screeningService;
